import type { ReactElement } from 'react';

import { ObstructionFormWizard } from '@/components/obstructions/wizard/ObstructionFormWizard';
import { useGeoMarkupStore } from '@/store';

/**
 * Thin wrapper around `ObstructionFormWizard` that re-applies the remount
 * key whenever `formMode + formEditingId` changes. The key forces the
 * wizard's internal state to reset, so editing an existing obstruction
 * never inherits transient state from a previous `new` draft (and vice
 * versa). Mirrors `TaskFormPanel`.
 *
 * In edit mode the loaded-detail id is folded into the key as well: the full
 * obstruction is fetched asynchronously, and Mantine `useForm` seeds its
 * `initialValues` only once at mount — so re-mounting when the detail arrives
 * is what populates the form with the existing obstruction's values.
 *
 * Lives in `src/artifacts/` next to `obstructions.ts` because the wrapper
 * is an implementation detail of the obstructions artifact registration —
 * kept in its own file so `obstructions.ts` (which exports only data)
 * does not trip the `react-refresh/only-export-components` rule.
 */
export function ObstructionFormPanel(): ReactElement {
  const formMode = useGeoMarkupStore((state) => state.obstructions.formMode);
  const formEditingId = useGeoMarkupStore(
    (state) => state.obstructions.formEditingId
  );
  const loadedDetailId = useGeoMarkupStore(
    (state) => state.obstructions.detailedObstruction?.id
  );
  const detailKeyPart =
    formMode === 'edit' ? `-${loadedDetailId ?? 'loading'}` : '';
  return (
    <ObstructionFormWizard
      key={`obstruction-form-${formMode ?? 'closed'}-${formEditingId ?? 'new'}${detailKeyPart}`}
    />
  );
}
